import React from 'react';
import { useFinancialPositionQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BalancePicture } from '@/api/models';

/**
 * The structural strip's slice of the Financial Position read.
 *
 * Shares the query key with the metric cards and the zone rail, so the strip's
 * working capital and the Net Working Capital card's difference come out of one
 * payload and cannot drift apart.
 */
export const useBalancePicture = () => {
    const query = useFinancialPositionQuery();

    const balance = React.useMemo<BalancePicture | null>(
        () => (query.data?.success ? query.data.response.balance : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    return {
        // A pending strip is not rendered at all, so the container only ever
        // needs the computed case.
        data: balance?.state === 'COMPUTED' ? balance : null,
        error,
        isLoading: query.isPending,
        onRetry,
    };
};
